import { Modal, Button, Alert, Form, Spinner } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { RiRadarLine } from 'react-icons/ri';
import { withNamespaces } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import ComplianceMonitoringService from 'src/modules/3rd-party-management/apis/admin/compliance/monitoring/MonitoringService';
import { toast } from 'react-toastify';

const Credit_Errors = {
    businesses_only: 'Failed to start compliance monitoring for the selected businesses due to insufficient KYB {{creditLabel}}. Please top up your balance and try again.',
    persons_only: 'Failed to start compliance monitoring for the selected individuals due to insufficient KYB {{creditLabel}}. Please top up your balance and try again.',
    businesses_and_persons: 'Failed to start compliance monitoring due to insufficient KYB {{creditLabel}}. Please ensure sufficient balance for both businesses and individuals and try again.'
};

const StartComplianceMonitoringModal = ({
    show,
    onClose,
    selectedRows,
    t
}) => {
    const [monitorType, setMonitorType] = useState(null);

    const {
        mutate: startMonitoringMutation,
        isLoading: startMonitoringLoading
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ComplianceMonitoringService.getInstance();

            return await service.addToMonitoringList(payload);
        },
        onSuccess: () => {
            toast(t("Compliance monitoring completed successfully for {{count}} third-party(ies).", {
                count: selectedRows.length
            }), {
                type: "success"
            });

            onClose();
        },
        onError: ({ response }) => {
            const {
                data: errorData
            } = response || {};

            if (errorData?.error === 'insufficient_credit_kyb') {
                toast(t(Credit_Errors[monitorType], {
                    creditLabel: "credit"
                }), {
                    type: "error"
                });
            }
            else {
                toast(t("Failed to start compliance monitoring for the selected third-party(ies)."), {
                    type: "error"
                });
            }
        }
    });

    const handleConfirm = () => {
        if (!monitorType) return;

        startMonitoringMutation({
            supplier_ids: selectedRows.map((r) => r.id),
            type: monitorType,
            provider: "company_info"
        });
    };

    useEffect(() => {
        if (show) {
            setMonitorType(null);
        }
    }, [show]);

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center justify-content-start">
                    <RiRadarLine className="me-2 text-primary" size={22} />
                    {t('Start {{complianceType}}', {
                        complianceType: 'Compliance Monitoring'
                    })}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                <p className="mb-3">
                    {t('You are about to start {{complianceType}} for the selected third-party(ies).', {
                        complianceType: 'Compliance Monitoring'
                    })}
                </p>

                <Alert variant="light" className="border">
                    <strong>{selectedRows.length}</strong> {t('{{count}} third-party(ies) will be added to monitoring.', {
                        count: ''
                    })}
                </Alert>

                <Form className="mt-3">

                    <Form.Label className="fw-bold">
                        {t('Select monitoring scope')}
                    </Form.Label>

                    <Form.Check
                        type="radio"
                        id="businesses_only"
                        name="monitorType"
                        label={t('Businesses Only')}
                        onChange={() => setMonitorType('businesses_only')}
                    />

                    <div className="text-muted small ms-4 mb-2">
                        {t('Only businesses will be monitored.')}
                    </div>

                    <Form.Check
                        type="radio"
                        id="persons_only"
                        name="monitorType"
                        label={t('Persons Only')}
                        onChange={() => setMonitorType('persons_only')}
                    />

                    <div className="text-muted small ms-4 mb-2">
                        {t('Only associated individuals will be monitored.')}
                    </div>

                    <Form.Check
                        type="radio"
                        id="businesses_and_persons"
                        name="monitorType"
                        label={t('Businesses and Persons')}
                        onChange={() => setMonitorType('businesses_and_persons')}
                    />

                    <div className="text-muted small ms-4">
                        {t('Both businesses and associated individuals will be monitored.')}
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer>

                <Button
                    variant="light"
                    onClick={onClose}
                    disabled={startMonitoringLoading}
                >
                    {t('Cancel')}
                </Button>

                <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={!monitorType || startMonitoringLoading}
                >
                    {startMonitoringLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('Starting Monitoring')}...
                        </>
                    ) : t("Start Monitoring")}
                </Button>

            </Modal.Footer>
        </Modal>
    );
}

export default withNamespaces()(
    StartComplianceMonitoringModal
);